import { BaseEntity } from '../base-entity/base-entity';
import { EntityPreviewMode } from '../base-entity-definition/entity-preview-mode';
import { Component, computed, effect, inject, input, InputSignal, OnDestroy, OnInit, signal, Signal, untracked, ViewChild, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ROUTER_OUTLET_DATA, RouterOutlet } from '@angular/router';
import { BaseEntityDescriptor } from '../base-entity/base-entity.descriptor';
import { AbstractAttrDescriptor } from '../base-entity/abstact-attr.descriptor';
import { BaseEntityAttrDescriptor } from '../base-entity/base-entity-attr.descriptor';
import { BaseFormHostDirective } from './base-form-host.directive';
import { MatCard, MatCardActions, MatCardContent } from '@angular/material/card';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { BaseUrlSegments } from '../base-form-navigator/base-url-segments';
import { BaseEntityFormBuilder } from './base-entity-form.builder';
import { NGXLogger } from 'ngx-logging-kit';
import { BaseFormNavigatorSingletonStore } from '../base-form-navigator/base-form-navigator.store';
import { BaseEntityStoreApi } from '../base-entity-store/base-entity.store';
import { EvaluatableRule, RULE_ENGINE, RuleEvaluationResult } from '../rule-engine/rule-engine';
import { RuleViolationsSingletonStore } from '../rule-engine/rule-violations.store';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'base-form',
  standalone: true,
  templateUrl: 'base-entity-form.component.html',
  styleUrls: ['./base-entity-form.css'],
  imports: [BaseFormHostDirective, NgTemplateOutlet, ReactiveFormsModule, MatCard, MatCardContent, MatCardActions, MatButton, MatIcon, RouterOutlet],
  providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } }],
})
export class BaseEntityFormComponent<Entity extends BaseEntity> implements OnInit, OnDestroy {
  baseEntityForm!: FormGroup;
  entityDescriptor = inject(ROUTER_OUTLET_DATA) as Signal<BaseEntityDescriptor>;
  entity: Signal<Entity> = computed(() => (this.isNewObject() ? this.store().createEntity() : (this.store().loadById(this.entityId()) as Entity)));
  entityId: InputSignal<string> = input.required<string>();
  isAbstract = computed(() => this.entityDescriptor().isAbstract);
  /** A preview of draft definitions over live objects: shown, never saved or deleted — see EntityPreviewMode. */
  /** The entity-wide read-only state: a kind of row that is never edited, or the preview of a draft. */
  entityReadOnly = computed(() => this.entityDescriptor().isReadOnly || this.previewMode.isReadOnly(this.entityDescriptor().entityName));
  /** Whether the row being shown may be changed; a new entity always may. See {@link BaseEntityDescriptorOptions.isEditable}. */
  rowEditable = computed(() => {
    const isEditable = this.entityDescriptor().isEditable;
    return this.isNewObject() || !isEditable || isEditable(this.entity());
  });
  /** Whether the row being shown may be deleted. See {@link BaseEntityDescriptorOptions.isDeletable}. */
  rowDeletable = computed(() => {
    const isDeletable = this.entityDescriptor().isDeletable;
    return this.isNewObject() || !isDeletable || isDeletable(this.entity());
  });
  isReadOnly = computed(() => this.entityReadOnly() || !this.rowEditable());
  isNewObject = computed(() => this.entityId() === BaseUrlSegments.NewEntity);
  store: Signal<BaseEntityStoreApi<Entity>> = computed(() => this.entityDescriptor().store as BaseEntityStoreApi<Entity>);
  @ViewChild(BaseFormHostDirective, { static: true, read: BaseFormHostDirective }) componentHost!: BaseFormHostDirective;

  /**
   * True while an embedded child's screen is open below this form. This form keeps running — that is what
   * carries its unsaved edits across the round trip — but it gives up the surface to the child.
   */
  readonly hasActiveEmbeddedChild: WritableSignal<boolean> = signal(false);
  violations: WritableSignal<RuleEvaluationResult[]> = signal([]);
  hasErrorViolations = computed(() => this.violations().some((v) => v.severity === 'ERROR'));
  // Reactive Save-button state: recomputes on any form value/status/pristine change so it also refreshes under
  // zoneless change detection, where a bare `baseEntityForm.dirty` binding stays stale after signal-less mutations
  // (e.g. the components-list navigator flow that only calls setValue()/markAsDirty()).
  saveDisabled!: Signal<boolean>;

  private readonly entityFormBuilder = inject(BaseEntityFormBuilder<Entity>);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly formNavigator = inject(BaseFormNavigatorSingletonStore);
  private readonly logger = inject(NGXLogger);
  private readonly ruleEngine = inject(RULE_ENGINE, { optional: true });
  private readonly violationsStore = inject(RuleViolationsSingletonStore);
  private readonly previewMode = inject(EntityPreviewMode);
  private rules: EvaluatableRule[] = [];
  private readonly fieldsWithRuleErrors = new Set<string>();
  private readonly ruleLoad$ = new Subject<void>();
  private ruleSubscription: Subscription | undefined;

  constructor() {
    this.baseEntityForm = this.formBuilder.group({});
    const formEvents = toSignal(this.baseEntityForm.events, { initialValue: null });
    this.saveDisabled = computed(() => {
      formEvents(); // establishes the reactive dependency; child setValue()/markAsDirty() bubble value/status events to the parent group
      return this.baseEntityForm.invalid || !this.baseEntityForm.dirty || this.isAbstract() || this.isReadOnly() || this.hasErrorViolations();
    });
    this.registerEffects();
  }

  // region angular lifecycle hooks
  ngOnInit(): void {
    this.formNavigator.setEntityName(this.entityDescriptor().entityName);
    this.formNavigator.determineActiveRouteSegment();
    this.logger.info('BaseEntityFormComponent initialized with: ', { entityDescriptor: this.entityDescriptor() });
    this.loadRules();
  }

  ngOnDestroy(): void {
    // Left without Save — by Cancel, or by navigating elsewhere. A Save that succeeded has already cleared the mark.
    void this.discardStagedChanges();
    this.ruleSubscription?.unsubscribe();
    this.ruleLoad$.complete();
    this.clearFieldRuleErrors();
    this.violationsStore.clearViolations();
  }
  // endregion

  // region event handlers
  async onCancel() {
    this.store().setCurrentEntity(undefined);
    await this.discardStagedChanges();
    await this.navigateBack();
  }

  async onDelete() {
    if (this.isReadOnly()) return;
    await this.store().delete(this.entityId());
    await this.navigateBack();
  }

  onFieldBlur(): void {
    this.evaluateRules();
  }

  async onSubmit() {
    // The button is disabled; Enter in a field submits the form regardless.
    if (this.isReadOnly()) return;
    const objectToSave = { ...this.entity(), ...this.baseEntityForm.value };
    this.evaluateRules(objectToSave);
    if (this.hasErrorViolations()) return;
    this.store().setCurrentEntity(undefined);
    const saved = this.isNewObject() ? await this.store().add(objectToSave) : await this.store().update(objectToSave);
    // Refused — by a rule the server enforces, a conflict, the network: the edits stay on the form, and the
    // store's error says why.
    if (saved === undefined) {
      if (!this.isNewObject()) this.store().setCurrentEntity(this.entityId());
      return;
    }
    // Saved: should navigating away fail, the form must not keep offering Save for edits already persisted.
    this.baseEntityForm.markAsPristine();
    await this.navigateBack();
  }
  // endregion

  // region protected, private helper methods
  /**
   * Drops the embedded-child changes staged in this entity's document (see `BaseEntityStoreApi.stage`) by
   * re-reading it, so an abandoned edit cannot later pass for a saved one. A new object has no document yet.
   */
  private async discardStagedChanges(): Promise<void> {
    if (this.isNewObject() || !this.store().hasStagedChanges(this.entityId())) return;
    await this.store().reload(this.entityId());
  }

  /**
   * Leaving an embedded child goes to the form of the entity that contains it. Only a fallback: a drill-down
   * already records where it came from. It is a page opened straight on its URL — a deep link, a reload —
   * that has nothing recorded, and would otherwise land on the child's own list.
   *
   * The navigator's entity name is re-claimed first: it is a singleton, and an embedded child opened below
   * this form — which outlives the child, so `ngOnInit` does not run again — leaves the child's name behind.
   * The list fallback would then aim at the child's list, which an embedded type does not have.
   */
  private async navigateBack(): Promise<void> {
    this.formNavigator.setEntityName(this.entityDescriptor().entityName);
    await this.formNavigator.navigateBack(this.entityDescriptor().isEmbedded ? this.formNavigator.determineOwnerUrl() : undefined);
  }

  private evaluateRules(candidate?: Entity | Record<string, unknown>): void {
    const ruleEngine = this.ruleEngine;
    if (!ruleEngine || this.rules.length === 0) return;
    const target = candidate ?? { ...this.entity(), ...this.baseEntityForm.value };
    const results = this.rules.map((rule) => ruleEngine.evaluate(target, rule));
    const failing = results.filter((r) => !r.passed);
    this.violations.set(failing);
    this.violationsStore.setViolations(this.entityDescriptor().entityName, failing);
    this.applyFieldRuleErrors(failing);
  }

  private applyFieldRuleErrors(violations: RuleEvaluationResult[]): void {
    this.clearFieldRuleErrors();
    const perField = new Map<string, RuleEvaluationResult[]>();
    for (const violation of violations) {
      for (const field of violation.fields ?? []) {
        const list = perField.get(field) ?? [];
        list.push(violation);
        perField.set(field, list);
      }
    }
    for (const [field, list] of perField) {
      const control = this.baseEntityForm.get(field);
      if (!control) continue;
      const message = list.map((v) => v.message ?? v.error ?? v.ruleId).join('\n');
      const severity = list.some((v) => v.severity === 'ERROR') ? 'ERROR' : list[0].severity;
      const existing = control.errors ?? {};
      control.setErrors({ ...existing, ruleViolation: { message, severity } });
      control.markAsTouched();
      this.fieldsWithRuleErrors.add(field);
    }
  }

  private clearFieldRuleErrors(): void {
    for (const field of this.fieldsWithRuleErrors) {
      const control = this.baseEntityForm.get(field);
      if (!control) continue;
      const errors = { ...control.errors };
      delete errors['ruleViolation'];
      control.setErrors(Object.keys(errors).length ? errors : null);
    }
    this.fieldsWithRuleErrors.clear();
  }

  private loadRules(): void {
    if (!this.ruleEngine) return;
    const context = this.entityDescriptor().entityName;
    this.ruleSubscription = this.ruleEngine.loadRulesFor(context).subscribe({
      next: (rules) => {
        this.rules = rules;
        this.logger.info(`BaseEntityFormComponent loaded ${rules.length} rule(s) for context "${context}"`);
      },
      error: (err) => this.logger.warn(`BaseEntityFormComponent failed to load rules for "${context}"`, err),
    });
  }

  /**
   * A key chosen at creation is fixed once the entity exists. Disabled controls are left out of the save, so the
   * key is not sent again, and the backend keeps the one it stored.
   */
  private disableCreateOnlyAttributes(): void {
    if (this.isNewObject()) return;
    const createOnly = (attrs: AbstractAttrDescriptor[]): string[] =>
      attrs.flatMap((attr) => {
        const nested = (attr as { attrDescriptors?: AbstractAttrDescriptor[] }).attrDescriptors ?? [];
        const own = attr instanceof BaseEntityAttrDescriptor && attr.createOnly ? [attr.attrName] : [];
        return [...own, ...createOnly(nested)];
      });
    createOnly(this.entityDescriptor().attrDescriptors).forEach((name) => this.baseEntityForm.get(name)?.disable({ emitEvent: false }));
  }

  private registerEffects(): void {
    effect(() => {
      if (this.entityId() && this.entityDescriptor() && this.entity())
        untracked(() => {
          const snapshot = this.formNavigator.popFormSnapshot(this.entityDescriptor().entityName);
          this.entityFormBuilder.buildForm(
            this.componentHost.viewContainerRef,
            this.baseEntityForm,
            this.store(),
            this.entityDescriptor().attrDescriptors,
            this.entity,
            this.entityDescriptor().entityName,
            snapshot,
          );
          this.disableCreateOnlyAttributes();
        });
    });

    // Keyed by the route segment, not by `entity().id`: the two agree for every entity the store keys by
    // `id` — the row was found by that segment — but an embedded row may carry no id at all, and passing
    // its `undefined` would clear the selection instead of making the row current.
    effect(() => {
      const entity = this.entity();
      // A new object has no row to be current. For a saved one the store is asked even when the loaded rows do not
      // hold it (it is beyond the first page of the list, or the list has not arrived), and finds it by its key.
      if (this.isNewObject()) this.store().setCurrentEntity(undefined);
      else if (entity || this.entityId()) this.store().setCurrentEntity(this.entityId());
    });
  }
  // endregion
}
